import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import Layout from "@/components/Layout";
import {
  Upload,
  FileText,
  Building2,
  Target,
  CheckCircle,
  AlertCircle,
  X,
  Eye,
  Zap
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { appelsOffresService, societesService, depotsService, analysesService } from "@/services";
import type { AppelOffre, Societe } from "@/types";

interface UploadFile {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'error';
  progress: number;
  error?: string;
  extractedText?: string;
  candidateName?: string;
}

const ImportCV = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [appelOffres, setAppelOffres] = useState<AppelOffre[]>([]);
  const [societes, setSocietes] = useState<Societe[]>([]);
  const [selectedAO, setSelectedAO] = useState(searchParams.get('ao') || '');
  const [selectedSociete, setSelectedSociete] = useState('');
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  const [viewingFile, setViewingFile] = useState<UploadFile | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [aoData, societeData] = await Promise.all([
        appelsOffresService.getAll(),
        societesService.getAll()
      ]);
      setAppelOffres(aoData);
      setSocietes(societeData);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      addFiles(selectedFiles);
    }
  };

  const addFiles = (newFiles: File[]) => {
    const validFiles = newFiles.filter(file => {
      const isValidType = file.type === 'application/pdf' || 
                         file.name.toLowerCase().endsWith('.pdf') ||
                         file.name.toLowerCase().endsWith('.docx');
      
      if (!isValidType) {
        toast({
          title: "Format non supporté",
          description: `Le fichier "${file.name}" n'est pas au format PDF ou DOCX`,
          variant: "destructive",
        });
        return false;
      }
      
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB max
      if (!isValidSize) {
        toast({
          title: "Fichier trop volumineux",
          description: `Le fichier "${file.name}" dépasse 10MB`,
          variant: "destructive",
        });
        return false;
      }
      
      return true;
    });

    const uploadFiles: UploadFile[] = validFiles.map(file => ({
      file,
      id: `${Date.now()}-${Math.random()}`,
      status: 'pending',
      progress: 0
    }));

    setFiles(prev => [...prev, ...uploadFiles]);
  };

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const processFile = async (uploadFile: UploadFile) => {
    if (!selectedAO || !selectedSociete) {
      toast({
        title: "Sélection manquante",
        description: "Veuillez sélectionner un appel d'offres et une société",
        variant: "destructive",
      });
      return;
    }

    try {
      // Étape 1: Upload du fichier
      setFiles(prev => prev.map(f =>
        f.id === uploadFile.id
          ? { ...f, status: 'uploading', progress: 25 }
          : f
      ));

      // Étape 2: Traitement via API (upload + extraction + création)
      setFiles(prev => prev.map(f =>
        f.id === uploadFile.id
          ? { ...f, status: 'processing', progress: 50 }
          : f
      ));

      const result = await depotsService.importCV(
        selectedAO,
        selectedSociete,
        uploadFile.file,
      );

      // Étape 3: Mise à jour du progrès
      setFiles(prev => prev.map(f =>
        f.id === uploadFile.id
          ? { ...f, progress: 75, candidateName: result.candidatName }
          : f
      ));

      // Succès !
      setFiles(prev => prev.map(f =>
        f.id === uploadFile.id
          ? {
              ...f,
              status: 'completed',
              progress: 100,
              extractedText: result.extractedText,
              candidateName: result.candidatName
            }
          : f
      ));

      toast({
        title: "Document importé avec succès",
        description: `Le CV de ${result.candidatName} a été traité`,
      });

    } catch (error) {
      console.error('Erreur lors du traitement du fichier:', error);

      setFiles(prev => prev.map(f =>
        f.id === uploadFile.id
          ? {
              ...f,
              status: 'error',
              error: error instanceof Error ? error.message : 'Erreur inconnue'
            }
          : f
      ));

      toast({
        title: "Erreur",
        description: `Impossible de traiter ${uploadFile.file.name}: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
        variant: "destructive",
      });
    }
  };

  const processAllFiles = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');

    if (pendingFiles.length === 0) {
      toast({
        title: "Aucun fichier à traiter",
        description: "Tous les fichiers ont déjà été traités",
        variant: "destructive",
      });
      return;
    }

    if (!selectedAO || !selectedSociete) {
      toast({
        title: "Sélection manquante",
        description: "Veuillez sélectionner un appel d'offres et une société",
        variant: "destructive",
      });
      return;
    }

    // Traiter les fichiers en parallèle (max 3 simultanés)
    const maxConcurrency = 3;
    const executing = new Set<Promise<void>>();
    for (const file of pendingFiles) {
      const p = processFile(file).then(() => { executing.delete(p); });
      executing.add(p);
      if (executing.size >= maxConcurrency) {
        await Promise.race(executing);
      }
    }
    await Promise.all(executing);
  };

  const generateAnalyses = async () => {
    const completedFiles = files.filter(f => f.status === 'completed');

    if (completedFiles.length === 0) {
      toast({
        title: "Aucun CV à analyser",
        description: "Aucun CV n'a été traité avec succès",
        variant: "destructive",
      });
      return;
    }

    if (!selectedAO) {
      toast({
        title: "Appel d'offres requis",
        description: "Veuillez sélectionner un appel d'offres",
        variant: "destructive",
      });
      return;
    }

    setIsAnalyzing(true);

    try {
      // Lancer les analyses pour tous les dépôts de l'appel d'offres
      const result = await analysesService.triggerBatch(selectedAO, false);

      toast({
        title: "Analyses terminées",
        description: `${result.successCount || 0} analyses réussies, ${result.errorCount || 0} erreurs`,
        variant: result.successCount > 0 ? "default" : "destructive",
      });

    } catch (error) {
      console.error('Erreur lors de la génération des analyses:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';

      // Détecter l'erreur de critères manquants
      if (errorMessage.includes("critère") || errorMessage.includes("criteria")) {
        toast({
          title: "Configuration requise",
          description: "Aucun critère d'évaluation configuré. Veuillez configurer les critères dans l'appel d'offres.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Erreur",
          description: `Impossible de générer les analyses: ${errorMessage}`,
          variant: "destructive",
        });
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getStatusIcon = (status: UploadFile['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-success" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-destructive" />;
      case 'uploading':
      case 'processing':
        return <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />;
      default:
        return <FileText className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: UploadFile['status']) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-success text-success-foreground">Traité</Badge>;
      case 'error':
        return <Badge variant="destructive">Erreur</Badge>;
      case 'uploading':
        return <Badge className="bg-primary text-primary-foreground">Upload...</Badge>;
      case 'processing':
        return <Badge className="bg-accent text-accent-foreground">Extraction...</Badge>;
      default:
        return <Badge variant="outline">En attente</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground">Import de CV</h1>
          <p className="text-muted-foreground">
            Importez et traitez automatiquement les candidatures
          </p>
        </div>

        {/* Sélection A.O. et Société */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Target className="h-5 w-5 mr-2" />
              Configuration
            </CardTitle>
            <CardDescription>
              Sélectionnez l'appel d'offres et la société candidates
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ao">Appel d'offres *</Label>
                <Select value={selectedAO} onValueChange={setSelectedAO}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un A.O." />
                  </SelectTrigger>
                  <SelectContent>
                    {appelOffres.map((ao) => (
                      <SelectItem key={ao.id} value={ao.id}>
                        {ao.titre} ({ao.reference})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="societe">Société *</Label>
                <Select value={selectedSociete} onValueChange={setSelectedSociete}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une société" />
                  </SelectTrigger>
                  <SelectContent>
                    {societes.map((societe) => (
                      <SelectItem key={societe.id} value={societe.id}>
                        {societe.nom} {societe.localite && `(${societe.localite})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

            </div>

            {societes.length === 0 && (
              <div className="text-center py-4">
                <p className="text-muted-foreground mb-4">
                  Aucune société disponible
                </p>
                <Button
                  variant="outline"
                  onClick={() => navigate('/societes')}
                >
                  <Building2 className="h-4 w-4 mr-2" />
                  Gérer les sociétés
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Zone de drop */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Upload className="h-5 w-5 mr-2" />
              Fichiers CV
            </CardTitle>
            <CardDescription>
              Formats supportés: PDF, DOCX (max 10MB par fichier)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/50'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                Glissez-déposez vos fichiers CV ici
              </h3>
              <p className="text-muted-foreground mb-4">
                ou cliquez pour sélectionner des fichiers
              </p>
              <input
                type="file"
                multiple
                accept=".pdf,.docx"
                onChange={handleFileSelect}
                className="hidden"
                id="file-input"
                capture={undefined}
              />
              <Button asChild variant="outline">
                <label htmlFor="file-input" className="cursor-pointer">
                  Parcourir les fichiers
                </label>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Liste des fichiers */}
        {files.length > 0 && (
          <Card className="card-shadow">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>
                  Fichiers sélectionnés ({files.length})
                </CardTitle>
                <div className="space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setFiles([])}
                  >
                    Vider la liste
                  </Button>
                  <Button
                    onClick={processAllFiles}
                    disabled={!selectedAO || !selectedSociete || files.every(f => f.status !== 'pending')}
                  >
                    Traiter tous les fichiers
                  </Button>
                  {files.some(f => f.status === 'pending') && (!selectedAO || !selectedSociete) && (
                    <p className="text-xs text-muted-foreground inline-block ml-2">
                      Sélectionnez un appel d'offres et une société pour démarrer le traitement.
                    </p>
                  )}
                  {files.some(f => f.status === 'pending') && selectedAO && selectedSociete && (
                    <p className="text-xs text-muted-foreground inline-block ml-2">
                      Cliquez sur « Traiter tous les fichiers » pour lancer l'import.
                    </p>
                  )}
                  {files.some(f => f.status === 'completed') && (
                    <Button
                      onClick={generateAnalyses}
                      disabled={isAnalyzing || !selectedAO}
                      className="bg-gradient-to-r from-primary to-accent text-primary-foreground"
                    >
                      <Zap className="h-4 w-4 mr-2" />
                      {isAnalyzing ? 'Analyse en cours...' : 'Générer les analyses'}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {files.map((uploadFile) => (
                  <div
                    key={uploadFile.id}
                    className="flex items-center justify-between p-4 border border-card-border rounded-lg"
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      {getStatusIcon(uploadFile.status)}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-foreground">{uploadFile.file.name}</h4>
                          {getStatusBadge(uploadFile.status)}
                        </div>
                        
                        <p className="text-sm text-muted-foreground">
                          <span className="font-medium">Fichier:</span> {uploadFile.file.name}
                        </p>
                        
                        {uploadFile.candidateName && (
                          <p className="text-sm text-muted-foreground">
                            <span className="font-medium">Candidat détecté:</span> {uploadFile.candidateName}
                          </p>
                        )}
                        
                        {uploadFile.error && (
                          <p className="text-sm text-destructive mt-1">
                            {uploadFile.error}
                          </p>
                        )}
                        
                        {(uploadFile.status === 'uploading' || uploadFile.status === 'processing') && (
                          <div className="mt-2">
                            <Progress value={uploadFile.progress} className="h-2" />
                            <p className="text-xs text-muted-foreground mt-1">
                              {uploadFile.status === 'uploading' ? 'Upload en cours...' : 'Extraction du texte...'}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {uploadFile.extractedText && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setViewingFile(uploadFile)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
                            <DialogHeader>
                              <DialogTitle>Contenu extrait - {uploadFile.file.name}</DialogTitle>
                              <DialogDescription>
                                Candidat: {uploadFile.candidateName}
                              </DialogDescription>
                            </DialogHeader>
                            <div className="overflow-y-auto max-h-[60vh] p-4 border rounded-md bg-muted/50">
                              <pre className="whitespace-pre-wrap text-sm font-mono">
                                {uploadFile.extractedText}
                              </pre>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(uploadFile.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default ImportCV;
